import React, { useState, useEffect } from 'react';
import { ChevronUp, ChevronDown, Plus, Download, Trash2 } from 'lucide-react';
import api from '../api/axios';
import Sqids from 'sqids';
import { generateCompliantPassword, validatePassword, passwordPolicyHint } from '../utils/passwordPolicy';
import { escapeCsvCell } from '../utils/csv';


const TimeZoneList = ({ selectedTimeZone, onChange }) => {
  const timezones = Intl.supportedValuesOf("timeZone");

  const regions = {
    Africa: timezones.filter((tz) => tz.startsWith("Africa/")),
    America: timezones.filter((tz) => tz.startsWith("America/")),
    Asia: timezones.filter((tz) => tz.startsWith("Asia/")),
  };

  const getOffset = (timeZone) => {
    const now = new Date();
    const tzString = now.toLocaleString("en-US", {
      timeZone,
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
    });
    const utcString = now.toLocaleString("en-US", {
      timeZone: "UTC",
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false
    });
    const tzDate = new Date(tzString);
    const utcDate = new Date(utcString);
    const offsetMinutes = (tzDate.getTime() - utcDate.getTime()) / 60000;
    const sign = offsetMinutes >= 0 ? "+" : "-";
    const absMinutes = Math.abs(offsetMinutes);
    const hours = String(Math.floor(absMinutes / 60)).padStart(2, "0");
    const minutes = String(absMinutes % 60).padStart(2, "0");
    return `${sign}${hours}:${minutes}`;
  };

  const renderRegionOptions = (regionName, tzArray) => {
    const options = [
      <option key={`${regionName}-header`} disabled style={{ fontWeight: "bold" }}>
        {regionName}
      </option>
    ];
    tzArray.forEach((tz) => {
      options.push(
        <option key={tz} value={tz}>
          {getOffset(tz)} {tz}
        </option>
      );
    });
    return options;
  };

  return (
    <select className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md" value={selectedTimeZone} onChange={(e) => onChange(e.target.value)}>
      <option value="UTC">+00:00 UTC</option>
      {renderRegionOptions("Africa", regions.Africa)}
      {renderRegionOptions("America", regions.America)}
      {renderRegionOptions("Asia", regions.Asia)}
    </select>
  );
};


const DistributorDetail = ({ selectedDistributor, setSelectedDistributor, setView, onSaved }) => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [selectedTimeZone, setSelectedTimeZone] = useState(selectedDistributor?.timeZone || 'Asia/Singapore');

  const handleGeneratePassword = () => {
    const generatedPassword = generateCompliantPassword();
    setPassword(generatedPassword);
    setConfirmPassword(generatedPassword);
    setError(null);
  };

  const handleSave = async () => {
    setError(null);
    setSuccessMessage(null);

    if (!selectedDistributor.distributorPk && !password) {
      setError('Password is required for a new distributor.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }
    if (password) {
      const policyError = validatePassword(password);
      if (policyError) {
        setError(policyError);
        return;
      }
    }
    if (!selectedDistributor.userId) {
      setError('Username is required.');
      return;
    }
    if (!selectedDistributor.company) {
      setError('Company Name is required.');
      return;
    }
    if (!selectedDistributor.userEmail) {
      setError('Email is required.');
      return;
    }
    if (!selectedDistributor.firstName) {
      setError('First name is required.');
      return;
    }
    if (!selectedDistributor.lastName) {
      setError('Last name is required.');
      return;
    }
    if (!selectedDistributor.address1) {
      setError('Address #1 is required.');
      return;
    }

    const payload = {
      userPk: selectedDistributor.userPk,
      distributorPk: selectedDistributor.distributorPk,
      userId: selectedDistributor.userId,
      active: selectedDistributor.active,
      userLocked: selectedDistributor.userLocked,
      accExpire: selectedDistributor.accExpire,
      accExpiryDateTime: selectedDistributor.accExpiryDateTime,
      userPwd: password,
      userCPwd: confirmPassword,
      firstname: selectedDistributor.firstName,
      lastname: selectedDistributor.lastName,
      userEmail: selectedDistributor.userEmail,
      timeZone: selectedTimeZone,
      companyName: selectedDistributor.company,
      contact: selectedDistributor.contact,
      address1: selectedDistributor.address1,
      address2: selectedDistributor.address2,
      city: selectedDistributor.city,
      state: selectedDistributor.state,
      postalCode: selectedDistributor.postalCode,
      country: selectedDistributor.country,
    };
    try {
      let response;
      if (selectedDistributor.distributorPk && selectedDistributor.userPk) {
        response = await api.put('/webapi/distributors', payload);
        setSuccessMessage('Distributor details updated successfully.');
      } else {
        response = await api.post('/webapi/distributors', payload);
        setSuccessMessage('New distributor added successfully.');
        setSelectedDistributor(prev => ({ ...prev, ...response.data }));
      }
      if (onSaved) onSaved();
    } catch (err) {
      console.error('Error saving distributor:', err);
      const apiError = err.response?.data?.error || err.response?.data?.message || 'Failed to save distributor details. Please try again.';
      setError(apiError);
    }
    const el = document.querySelector('main');
    if (el) {
      el.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen ">
      <div >
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-gray-800 mb-1">Distributor</h1>
          <div className="flex gap-4 text-sm">
            <button
              onClick={() => setView('list')}
              className="text-indigo-600 hover:text-indigo-800"
            >
              Distributor Listing
            </button>
            <span className="text-gray-400">/</span>
            <span className="text-gray-600">Distributor Profile</span>
          </div>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}

        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Success: </strong>
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6">
          <h2 className="text-lg font-semibold text-gray-800 mb-6">Login Access</h2>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">Username</label>
            <input
              type="text"
              autoComplete="off"
              value={selectedDistributor?.userId ?? ''}
              onChange={(e) =>
                setSelectedDistributor(prev => ({ ...prev, userId: e.target.value }))
              }
              className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              disabled={!!selectedDistributor?.distributorPk}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <div className="flex items-center justify-between mb-2 gap-3">
                <label className="block text-sm text-gray-600">Password</label>
                <button
                  type="button"
                  onClick={handleGeneratePassword}
                  className="text-sm font-medium text-indigo-600 hover:text-indigo-700"
                >
                  Generate
                </button>
              </div>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoComplete="off"
                  className="w-full px-4 py-2 pr-10 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">Confirm password</label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  placeholder="Confirm password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  autoComplete="off"
                  className="w-full px-4 py-2 pr-10 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-2 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg>
                  ) : (
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                  )}
                </button>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500 mb-4">
            {passwordPolicyHint}
          </p>

          <div className="mb-4">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedDistributor?.active === 'Y'}
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, active: e.target.checked ? 'Y' : 'N' }))}
                className="w-4 h-4 text-indigo-600"
              />
              <span className="text-sm text-gray-700">Active - Account status</span>
            </label>
          </div>

          <div className="mb-6">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={selectedDistributor?.userLocked === 1}
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, userLocked: e.target.checked ? 1 : 0 }))}
                className="w-4 h-4 text-red-600"
              />
              <span className="text-sm text-gray-700">Locked - Account locked</span>
            </label>
          </div>

          <div className="mb-4">
            <label className="flex items-center gap-2 text-sm text-gray-700 mb-2">
              <input
                type="checkbox"
                checked={selectedDistributor?.accExpire === 'Y'}
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, accExpire: e.target.checked ? 'Y' : 'N' }))}
                className="w-4 h-4"
              />
              Account expires
            </label>
            <input
              type="date"
              placeholder="Expiry date"
              className="px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={selectedDistributor?.accExpiryDateTime ?? ''}
              onChange={(e) =>
                setSelectedDistributor(prev => ({ ...prev, accExpiryDateTime: e.target.value }))
              }
            />
          </div>

          <div className="h-1 mb-4 bg-[radial-gradient(circle_at_center,theme(colors.red.300)_1px,transparent_1px)] [background-size:6px_6px] bg-repeat-x"></div>

          <h3 className="text-lg font-semibold text-gray-800 mb-6">Info and Preference</h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">First name</label>
              <input
                type="text"
                autoComplete="off"
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent rounded-md"
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, firstName: e.target.value }))
                }
                value={selectedDistributor?.firstName || ''}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">Last name / surname</label>
              <input
                type="text"
                autoComplete="off"
                value={selectedDistributor?.lastName ?? ''}
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, lastName: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent rounded-md"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">New email address</label>
              <input
                type="email"
                value={selectedDistributor?.userEmail ?? ''}
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, userEmail: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">Contact</label>
              <input
                type="text"
                autoComplete="off"
                value={selectedDistributor?.contact ?? ''}
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, contact: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">Time Zone</label>
            <TimeZoneList
              selectedTimeZone={selectedTimeZone}
              onChange={setSelectedTimeZone}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">Company Name <span className="text-xs text-blue-500">(required)</span></label>
            <input
              type="text"
              autoComplete="off"
              value={selectedDistributor?.company || ''}
              onChange={(e) =>
                setSelectedDistributor(prev => ({ ...prev, company: e.target.value }))
              }
              className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">Address #1 <span className="text-xs text-blue-500">(required)</span></label>
            <input
              type="text"
              autoComplete="off"
              value={selectedDistributor?.address1 ?? ''}
              onChange={(e) =>
                setSelectedDistributor(prev => ({ ...prev, address1: e.target.value }))}
              className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm text-gray-600 mb-2">Address #2</label>
            <input
              type="text"
              autoComplete="off"
              className="w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              value={selectedDistributor?.address2 ?? ''}
              onChange={(e) =>
                setSelectedDistributor(prev => ({ ...prev, address2: e.target.value }))}
            />
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">Country</label>
              <select
                value={selectedDistributor?.country || ''}
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, country: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              >
                <option value="Australia">Australia</option>
                <option value="China">China</option>
                <option value="France">France</option>
                <option value="Germany">Germany</option>
                <option value="Hong Kong">Hong Kong</option>
                <option value="Indonesia">Indonesia</option>
                <option value="Japan">Japan</option>
                <option value="Malaysia">Malaysia</option>
                <option value="Myanmar">Myanmar</option>
                <option value="Philippines">Philippines</option>
                <option value="Qatar">Qatar</option>
                <option value="Saudi Arabia">Saudi Arabia</option>
                <option value="Singapore">Singapore</option>
                <option value="Thailand">Thailand</option>
                <option value="United Arab Emirates">United Arab Emirates</option>
                <option value="United Kingdom">United Kingdom</option>
                <option value="Vietnam">Vietnam</option>
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">State</label>
              <input
                type="text"
                autoComplete="off"
                className="w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={selectedDistributor?.state ?? ''}
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, state: e.target.value }))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm text-gray-600 mb-2">City/Town</label>
              <input
                type="text"
                autoComplete="off"
                className="w-full px-4 py-2 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                value={selectedDistributor?.city ?? ''}
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, city: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-600 mb-2">Zip / Postal Code</label>
              <input
                type="text"
                autoComplete="off"
                value={selectedDistributor?.postalCode ?? ''}
                onChange={(e) =>
                  setSelectedDistributor(prev => ({ ...prev, postalCode: e.target.value }))}
                className="w-full px-4 py-2 bg-gray-50 border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="flex gap-4 mt-6">
          <button
            onClick={handleSave}
            className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700"
          >
            Save
          </button>
          <button
            onClick={() => setView('list')}
            className="bg-indigo-600 text-white px-6 py-2 rounded-md hover:bg-indigo-700"
          >
            Back
          </button>
        </div>
      </div>
    </div>
  );
};


const DistributorsManagement = () => {
  const [view, setView] = useState('list');
  const [selectedDistributor, setSelectedDistributor] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [distributors, setDistributors] = useState([]);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const sqids = new Sqids({
    alphabet: 'abcdefghijklmnopqrstuvwxyz@#%$&*0123456789',
    minLength: 10
  });

  useEffect(() => {
    const fetchDistributors = async () => {
      try {
        setError(null);
        const response = await api.get('/webapi/distributors');
        if (response.data) {
          setDistributors(response.data.distributors || response.data || []);
        }
      } catch (err) {
        console.error('Error fetching distributors:', err);
        setError('Failed to fetch distributor list. Please try again later.');
        setDistributors([]);
      }
    };

    if (view === 'list') {
      fetchDistributors();
    }
  }, [view, refreshKey]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedDistributors = [...distributors].sort((a, b) => {
    if (sortConfig.key) {
      const aVal = a[sortConfig.key];
      const bVal = b[sortConfig.key];
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
    }
    return 0;
  });

  const SortIcon = ({ columnKey }) => {
    if (sortConfig.key !== columnKey) {
      return <ChevronUp className="w-4 h-4 text-gray-300" />;
    }
    return sortConfig.direction === 'asc' ?
      <ChevronUp className="w-4 h-4 text-gray-600" /> :
      <ChevronDown className="w-4 h-4 text-gray-600" />;
  };

  const handleEditClick = (distributor) => {
    setSuccessMessage(null);
    setSelectedDistributor(distributor);
    setView('detail');
  };

  const handleDelete = async (distributor) => {
    if (!window.confirm(`Are you sure you want to delete distributor "${distributor.userId}"?`)) {
      return;
    }
    try {
      setError(null);
      await api.delete('/webapi/distributors', {
        data: {
          distributorPk: distributor.distributorPk,
          userPk: distributor.userPk,
        }
      });
      setSuccessMessage(`Distributor "${distributor.userId}" deleted.`);
      setRefreshKey(k => k + 1);
    } catch (err) {
      console.error('Error deleting distributor:', err);
      const apiError = err.response?.data?.error || err.response?.data?.message || 'Failed to delete distributor.';
      setError(apiError);
    }
  };

  const handleDownloadCSV = () => {
    if (!distributors || distributors.length === 0) {
      setError('No distributors to download.');
      return;
    }

    const headers = ['Username', 'Name', 'Company', 'Email', 'Country', 'Status'];
    const csvContent = [
      headers.join(','),
      ...sortedDistributors.map(d => {
        return [
          escapeCsvCell(d.userId),
          escapeCsvCell(`${d.firstName || ''} ${d.lastName || ''}`.trim()),
          escapeCsvCell(d.company),
          escapeCsvCell(d.userEmail),
          escapeCsvCell(d.country),
          escapeCsvCell(d.status)
        ].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `distributors_list_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const DistributorsList = () => (
    <div className="min-h-screen ">
      <div >
        <div className="mb-6">
          <h1 className="text-3xl font-semibold text-gray-800 mb-1">Distributors</h1>
          <p className="text-gray-500">Account list</p>
        </div>
        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Error: </strong>
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        {successMessage && (
          <div className="bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative mb-6" role="alert">
            <strong className="font-bold">Success: </strong>
            <span className="block sm:inline">{successMessage}</span>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <p className="text-sm text-gray-500 mb-1">Registered Distributors</p>
          <p className="text-3xl font-semibold text-gray-800">{distributors.length}</p>
        </div>

        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex gap-4 mb-6">
            <button
              onClick={() => {
                setSuccessMessage(null);
                setSelectedDistributor({
                  userId: '',
                  firstName: '',
                  lastName: '',
                  company: '',
                  userEmail: '',
                  contact: '',
                  country: 'Singapore',
                  address1: '',
                  address2: '',
                  city: '',
                  state: '',
                  postalCode: '',
                  active: 'Y',
                  userLocked: 0,
                  accExpire: 'N',
                  accExpiryDateTime: '',
                  distributorPk: '',
                  userPk: '',
                  status: 'Active'
                });
                setView('detail');
              }}
              className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Distributor
            </button>
            <button
              onClick={handleDownloadCSV}
              className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              CSV
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    <button onClick={() => handleSort('firstName')} className="flex items-center gap-1 hover:text-gray-900">
                      USER <SortIcon columnKey="firstName" />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    <button onClick={() => handleSort('country')} className="flex items-center gap-1 hover:text-gray-900">
                      Country <SortIcon columnKey="country" />
                    </button>
                  </th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">
                    <button onClick={() => handleSort('status')} className="flex items-center gap-1 hover:text-gray-900">
                      Status <SortIcon columnKey="status" />
                    </button>
                  </th>
                  {/* <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Action</th> */}
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">QR Code</th>
                </tr>
              </thead>
              <tbody>
                {sortedDistributors.map((distributor, index) => (
                  <tr
                    key={index}
                    className="border-b border-gray-100 hover:bg-gray-50"
                  >
                    <td
                      className="py-4 px-4 cursor-pointer"
                      onClick={() => handleEditClick(distributor)}
                    >
                      <div className="font-medium text-gray-800">{(distributor.firstName || '') + ' ' + (distributor.lastName || '')} [{distributor.userId}]</div>
                      <div className="text-sm text-gray-500">{distributor.company}</div>
                      <div className="text-sm text-gray-500">{distributor.userEmail}</div>
                    </td>
                    <td className="py-4 px-4 text-gray-700">
                      {distributor.country}
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <span className={`${distributor.status === 'Active' ? 'text-green-600' : 'text-orange-600'} font-medium`}>
                          {distributor.status}
                        </span>
                      </div>
                    </td>
                    {/* <td className="py-4 px-4">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleEditClick(distributor)}
                          className="bg-indigo-600 text-white text-xs px-2 py-1 rounded cursor-pointer hover:bg-indigo-700"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDelete(distributor)}
                          className="bg-red-600 text-white text-xs px-2 py-1 rounded cursor-pointer hover:bg-red-700 flex items-center gap-1"
                        >
                          <Trash2 className="w-3 h-3" />
                          Delete
                        </button>
                      </div>
                    </td> */}
                    <td className="px-6 py-4 text-gray-800">
                      <a href={'/webapi/qrcode?Userpk=' + encodeURIComponent(sqids.encode([distributor?.userPk]))} target="_blank" rel="noopener noreferrer">view</a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );

  if (view === 'list') {
    return <DistributorsList />;
  }
  return (
    <DistributorDetail
      selectedDistributor={selectedDistributor}
      setSelectedDistributor={setSelectedDistributor}
      setView={setView}
      onSaved={() => setRefreshKey(k => k + 1)}
    />
  );
};


export default DistributorsManagement;
